/* =====================================================================
   PEOPLE GET UP — Master Theme JavaScript
   Vanilla ES6, no dependencies. Wrapped in an IIFE, defensive (null checks).
   Implements every JS BEHAVIOR HOOK from the design contract plus a
   Shopify-ready cart module with a preview-mode fallback.
   ===================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Small utilities                                                     */
  /* ------------------------------------------------------------------ */
  var $  = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  };
  var on = function (el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts || false); };

  var prefersReducedMotion = function () {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };
  var isTouch = function () {
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  };

  // Money formatter (USD). Shopify prices come back as integer cents.
  var formatMoney = function (cents) {
    var n = (Number(cents) || 0) / 100;
    return '$' + n.toFixed(2);
  };

  // Focusable selector for basic focus-trapping.
  var FOCUSABLE =
    'a[href],button:not([disabled]),textarea:not([disabled]),' +
    'input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------ */
  /* MODULE: Sticky / shrink + hide-on-scroll-down header                */
  /* ------------------------------------------------------------------ */
  function initHeader() {
    var header = $('[data-pgu-header]');
    if (!header) return;

    var lastY = window.pageYOffset || 0;
    var ticking = false;
    var SHRINK_AT = 40;     // px scrolled before shrink/blur state
    var HIDE_DELTA = 6;     // ignore tiny scroll jitter

    function update() {
      var y = window.pageYOffset || 0;

      // Shrink + blurred dark bg after 40px.
      if (y > SHRINK_AT) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');

      // Hide on scroll down, show on scroll up (keep visible near top).
      if (Math.abs(y - lastY) > HIDE_DELTA) {
        if (y > lastY && y > 120) header.classList.add('is-hidden');
        else header.classList.remove('is-hidden');
        lastY = y;
      }
      ticking = false;
    }

    on(window, 'scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Mobile nav (toggle + body lock + basic focus trap + Esc)    */
  /* ------------------------------------------------------------------ */
  function initMobileNav() {
    var burger = $('[data-pgu-burger]');
    var nav = $('[data-pgu-mobile-nav]');
    if (!nav) return;

    var closeEls = $$('[data-pgu-mobile-close]', nav);
    var lastFocused = null;

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var focusables = $$(FOCUSABLE, nav).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      nav.classList.add('is-open');
      document.body.classList.add('pgu-noscroll');
      if (burger) burger.setAttribute('aria-expanded', 'true');
      nav.setAttribute('aria-hidden', 'false');
      var firstLink = $(FOCUSABLE, nav);
      if (firstLink) firstLink.focus();
      document.addEventListener('keydown', trapFocus);
    }

    function close() {
      nav.classList.remove('is-open');
      document.body.classList.remove('pgu-noscroll');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', trapFocus);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    on(burger, 'click', function () {
      if (nav.classList.contains('is-open')) close();
      else open();
    });
    closeEls.forEach(function (el) { on(el, 'click', close); });

    // Close when a nav link is clicked (anchor navigation).
    $$('.pgu-mobile-nav__link', nav).forEach(function (link) {
      on(link, 'click', close);
    });

    // Expose close so the global Esc handler can reach it.
    nav.__pguClose = close;
    nav.__pguIsOpen = function () { return nav.classList.contains('is-open'); };
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Cart drawer (open/close, overlay, Esc)                      */
  /* ------------------------------------------------------------------ */
  function initDrawer() {
    var drawer = $('[data-pgu-drawer]');
    if (!drawer) return;

    var openers = $$('[data-pgu-cart-open]');
    var closers = $$('[data-pgu-cart-close]', drawer);
    var overlay = $('.pgu-drawer__overlay', drawer);
    var lastFocused = null;

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var focusables = $$(FOCUSABLE, drawer).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      document.body.classList.add('pgu-noscroll');
      drawer.setAttribute('aria-hidden', 'false');
      var firstClose = $('[data-pgu-cart-close]', drawer);
      if (firstClose) firstClose.focus();
      document.addEventListener('keydown', trapFocus);
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('pgu-noscroll');
      drawer.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', trapFocus);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    openers.forEach(function (el) {
      on(el, 'click', function (e) { e.preventDefault(); open(); });
    });
    closers.forEach(function (el) { on(el, 'click', close); });
    on(overlay, 'click', close);

    // Expose to other modules (cart add opens the drawer).
    drawer.__pguOpen = open;
    drawer.__pguClose = close;
    drawer.__pguIsOpen = function () { return drawer.classList.contains('is-open'); };
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Global Esc — closes drawer + mobile nav                     */
  /* ------------------------------------------------------------------ */
  function initEscClose() {
    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      var drawer = $('[data-pgu-drawer]');
      if (drawer && drawer.__pguIsOpen && drawer.__pguIsOpen()) {
        drawer.__pguClose();
        return;
      }
      var nav = $('[data-pgu-mobile-nav]');
      if (nav && nav.__pguIsOpen && nav.__pguIsOpen()) {
        nav.__pguClose();
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Scroll reveals (IntersectionObserver + stagger)             */
  /* ------------------------------------------------------------------ */
  function initReveals() {
    var els = $$('[data-reveal]');
    if (!els.length) return;

    // Reduced motion: just show everything, no transforms/observers.
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay'), 10) || 0;
        if (delay) {
          el.style.transitionDelay = delay + 'ms';
          // Clear the delay after it fires so re-layouts don't re-stagger.
          window.setTimeout(function () { el.style.transitionDelay = ''; }, delay + 50);
        }
        el.classList.add('is-visible');
        obs.unobserve(el);
      });
    }, { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    els.forEach(function (el) { observer.observe(el); });

    // Hero/above-the-fold content reveals immediately on load (a deliberate
    // entrance) so the banner never paints empty while waiting on the observer.
    window.requestAnimationFrame(function () {
      $$('.pgu-hero [data-reveal]').forEach(function (el, i) {
        el.style.transitionDelay = (i * 70) + 'ms';
        el.classList.add('is-visible');
        observer.unobserve(el);
        window.setTimeout(function () { el.style.transitionDelay = ''; }, (i * 70) + 50);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Soft parallax (rAF) — lifestyle band only                   */
  /* ------------------------------------------------------------------ */
  function initParallax() {
    var els = $$('[data-parallax]');
    if (!els.length) return;

    // Disabled under reduced-motion, on touch, and on narrow viewports.
    if (prefersReducedMotion() || isTouch() || window.innerWidth < 768) return;

    var MAX = 40; // clamp the translate to a small premium amount.
    var ticking = false;

    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        // progress: -1 (below) -> 1 (above) as the element passes through.
        var progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        var offset = Math.max(-MAX, Math.min(MAX, progress * -MAX));
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }

    on(window, 'scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    on(window, 'resize', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    });

    update();
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Accordions (aria-expanded toggle)                           */
  /* ------------------------------------------------------------------ */
  function initAccordions() {
    var heads = $$('[data-pgu-accordion]');
    if (!heads.length) return;

    heads.forEach(function (head) {
      var item = head.closest('.pgu-accordion__item');
      if (!item) return;
      var body = $('.pgu-accordion__body', item);

      // Initialise ARIA state from current open class.
      var open = item.classList.contains('is-open');
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (body) body.setAttribute('aria-hidden', open ? 'false' : 'true');

      on(head, 'click', function () {
        var isOpen = item.classList.toggle('is-open');
        head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (body) body.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Quantity steppers                                           */
  /* ------------------------------------------------------------------ */
  function initQtySteppers() {
    function findInput(btn) {
      var wrap = btn.closest('.pgu-qty, .pgu-pdp__qtyrow') || btn.parentNode;
      return wrap ? $('[data-pgu-qty-input]', wrap) : null;
    }
    function adjust(input, delta) {
      if (!input) return;
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 1;
      var val = parseInt(input.value, 10);
      if (isNaN(val)) val = min;
      val = Math.max(min, val + delta);
      input.value = val;
      // Notify any listeners (e.g. line-item change handlers).
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    $$('[data-pgu-qty-minus]').forEach(function (btn) {
      on(btn, 'click', function () { adjust(findInput(btn), -1); });
    });
    $$('[data-pgu-qty-plus]').forEach(function (btn) {
      on(btn, 'click', function () { adjust(findInput(btn), 1); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: PDP gallery thumb swap + active state                       */
  /* ------------------------------------------------------------------ */
  function initGallery() {
    var thumbs = $$('[data-pgu-thumb]');
    if (!thumbs.length) return;
    var stage = $('.pgu-pdp__stageimg');

    thumbs.forEach(function (thumb) {
      on(thumb, 'click', function () {
        var src = thumb.getAttribute('data-pgu-thumb') ||
                  (thumb.querySelector('img') && thumb.querySelector('img').src);
        if (stage && src) {
          stage.src = src;
          var alt = thumb.getAttribute('data-alt');
          if (alt) stage.alt = alt;
        }
        thumbs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-selected', 'true');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: PDP hover zoom (desktop only)                               */
  /* ------------------------------------------------------------------ */
  function initZoom() {
    var zones = $$('[data-pgu-zoom]');
    if (!zones.length) return;
    if (isTouch() || window.innerWidth < 768) return;

    zones.forEach(function (zone) {
      var img = $('img', zone) || $('.pgu-pdp__stageimg', zone);
      if (!img) return;

      on(zone, 'mouseenter', function () { zone.classList.add('is-zooming'); });
      on(zone, 'mousemove', function (e) {
        var rect = zone.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        x = Math.max(0, Math.min(100, x));
        y = Math.max(0, Math.min(100, y));
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      on(zone, 'mouseleave', function () {
        zone.classList.remove('is-zooming');
        img.style.transformOrigin = 'center center';
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* MODULE: Marquee duplication (seamless loop)                         */
  /* ------------------------------------------------------------------ */
  function initMarquee() {
    var marquees = $$('[data-pgu-marquee]');
    if (!marquees.length) return;

    marquees.forEach(function (track) {
      if (track.getAttribute('data-pgu-marquee-done') === 'true') return;
      // Duplicate the existing content once so the CSS loop is seamless.
      var clone = track.innerHTML;
      track.innerHTML = clone + clone;
      track.setAttribute('data-pgu-marquee-done', 'true');
      track.setAttribute('aria-hidden', 'true');
    });
  }

  /* ================================================================== */
  /* CART MODULE — Shopify AJAX with a preview-mode fallback             */
  /* ================================================================== */
  var Cart = (function () {

    // Detect Shopify context: routes object present and not a forced preview.
    var hasShopify = function () {
      return typeof window.Shopify !== 'undefined' &&
             !document.documentElement.hasAttribute('data-pgu-preview');
    };

    /* ---- DOM refs (lazily resolved) ---- */
    function drawer() { return $('[data-pgu-drawer]'); }
    function body()   { return $('.pgu-drawer__body'); }
    function countEls() { return $$('.pgu-cart-count'); }
    function subtotalEl() { return $('.pgu-drawer__subtotal'); }
    function emptyEl() { return $('.pgu-drawer__empty'); }

    function setCount(n) {
      countEls().forEach(function (el) {
        el.textContent = n;
        el.classList.toggle('is-empty', n <= 0);
      });
    }

    function openDrawer() {
      var d = drawer();
      if (d && d.__pguOpen) d.__pguOpen();
    }

    /* ---------------- Shopify-backed rendering ---------------- */

    // Build a single line-item element from a Shopify cart line.
    function renderLine(item) {
      var line = document.createElement('div');
      line.className = 'pgu-line';
      line.setAttribute('data-pgu-line', '');
      line.setAttribute('data-key', item.key || '');

      var img = (item.image || item.featured_image && item.featured_image.url) || '';
      line.innerHTML =
        '<img class="pgu-line__img" src="' + img + '" alt="' +
          (item.product_title || item.title || '') + '" width="72" height="72" loading="lazy">' +
        '<div class="pgu-line__info">' +
          '<p class="pgu-line__title">' + (item.product_title || item.title || '') + '</p>' +
          '<p class="pgu-line__price">' + formatMoney(item.final_price || item.price) + '</p>' +
          '<div class="pgu-qty" data-pgu-line-qty>' +
            '<button type="button" class="pgu-qty__btn" data-pgu-line-minus aria-label="Decrease quantity">&minus;</button>' +
            '<span class="pgu-qty__val">' + (item.quantity || 1) + '</span>' +
            '<button type="button" class="pgu-qty__btn" data-pgu-line-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="pgu-line__remove" data-pgu-line-remove aria-label="Remove item">Remove</button>';

      // Wire line-level quantity + remove against /cart/change.js.
      var key = item.key;
      var qtyVal = item.quantity || 1;
      on($('[data-pgu-line-minus]', line), 'click', function () {
        changeLine(key, Math.max(0, qtyVal - 1));
      });
      on($('[data-pgu-line-plus]', line), 'click', function () {
        changeLine(key, qtyVal + 1);
      });
      on($('[data-pgu-line-remove]', line), 'click', function () {
        changeLine(key, 0);
      });

      return line;
    }

    // Re-render the whole drawer body from a Shopify cart object.
    function renderCart(cart) {
      if (!cart) return;
      setCount(cart.item_count || 0);
      var sub = subtotalEl();
      if (sub) sub.textContent = formatMoney(cart.total_price || 0);

      var b = body();
      if (!b) return;
      // Clear previous lines (preserve any non-line markup like upsell).
      $$('[data-pgu-line]', b).forEach(function (l) { l.remove(); });

      var empty = emptyEl();
      if (!cart.items || !cart.items.length) {
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;

      var frag = document.createDocumentFragment();
      cart.items.forEach(function (item) { frag.appendChild(renderLine(item)); });
      // Insert before upsell/empty markers when present, else append.
      var anchor = $('.pgu-drawer__upsell', b) || empty;
      if (anchor) b.insertBefore(frag, anchor);
      else b.appendChild(frag);
    }

    /* ---------------- Shopify AJAX endpoints ---------------- */

    function addToCart(payload) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add.js failed: ' + res.status);
          return res.json();
        })
        .then(function () { return refreshCart(); })
        .then(function (cart) { openDrawer(); return cart; });
    }

    function refreshCart() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('cart.js failed: ' + res.status);
          return res.json();
        })
        .then(function (cart) { renderCart(cart); return cart; });
    }

    function changeLine(key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('change.js failed: ' + res.status);
          return res.json();
        })
        .then(function (cart) { renderCart(cart); return cart; });
    }

    /* ---------------- Preview-mode fallback ---------------- */
    // Simulates add-to-cart with no Shopify backend: clones a line into the
    // drawer, updates count + subtotal, and opens the drawer.

    var previewState = { lines: {}, count: 0 };

    function previewRecalc() {
      var subtotal = 0;
      var count = 0;
      Object.keys(previewState.lines).forEach(function (id) {
        var l = previewState.lines[id];
        subtotal += l.price * l.qty;
        count += l.qty;
      });
      previewState.count = count;
      setCount(count);
      var sub = subtotalEl();
      if (sub) sub.textContent = formatMoney(subtotal);
      var empty = emptyEl();
      if (empty) empty.hidden = count > 0;
    }

    function previewRenderLine(id) {
      var l = previewState.lines[id];
      var b = body();
      if (!b || !l) return;

      var existing = $('[data-pgu-line][data-key="' + id + '"]', b);
      if (existing) {
        var valEl = $('.pgu-qty__val', existing);
        if (valEl) valEl.textContent = l.qty;
        var priceEl = $('.pgu-line__price', existing);
        if (priceEl) priceEl.textContent = formatMoney(l.price);
        return;
      }

      var line = document.createElement('div');
      line.className = 'pgu-line';
      line.setAttribute('data-pgu-line', '');
      line.setAttribute('data-key', id);
      line.innerHTML =
        '<img class="pgu-line__img" src="' + l.img + '" alt="' + l.title +
          '" width="72" height="72" loading="lazy">' +
        '<div class="pgu-line__info">' +
          '<p class="pgu-line__title">' + l.title + '</p>' +
          '<p class="pgu-line__price">' + formatMoney(l.price) + '</p>' +
          '<div class="pgu-qty" data-pgu-line-qty>' +
            '<button type="button" class="pgu-qty__btn" data-pgu-line-minus aria-label="Decrease quantity">&minus;</button>' +
            '<span class="pgu-qty__val">' + l.qty + '</span>' +
            '<button type="button" class="pgu-qty__btn" data-pgu-line-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="pgu-line__remove" data-pgu-line-remove aria-label="Remove item">Remove</button>';

      on($('[data-pgu-line-minus]', line), 'click', function () {
        l.qty = Math.max(0, l.qty - 1);
        if (l.qty === 0) { line.remove(); delete previewState.lines[id]; }
        else { var v = $('.pgu-qty__val', line); if (v) v.textContent = l.qty; }
        previewRecalc();
      });
      on($('[data-pgu-line-plus]', line), 'click', function () {
        l.qty += 1;
        var v = $('.pgu-qty__val', line); if (v) v.textContent = l.qty;
        previewRecalc();
      });
      on($('[data-pgu-line-remove]', line), 'click', function () {
        line.remove();
        delete previewState.lines[id];
        previewRecalc();
      });

      var anchor = $('.pgu-drawer__upsell', b) || emptyEl();
      if (anchor) b.insertBefore(line, anchor);
      else b.appendChild(line);
    }

    function previewAdd(data) {
      var id = data.id || data.title;
      if (previewState.lines[id]) {
        previewState.lines[id].qty += (data.qty || 1);
      } else {
        previewState.lines[id] = {
          title: data.title || 'Product',
          price: Number(data.price) || 0, // cents
          img: data.img || '',
          qty: data.qty || 1
        };
      }
      previewRenderLine(id);
      previewRecalc();
      openDrawer();
    }

    /* ---------------- Public binding ---------------- */
    function bind() {
      // Preview add buttons: read product data from data-attributes.
      $$('[data-pgu-add]').forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();

          if (hasShopify() && btn.getAttribute('data-variant-id')) {
            // Real Shopify add when a variant id is present.
            addToCart({
              id: btn.getAttribute('data-variant-id'),
              quantity: parseInt(btn.getAttribute('data-qty'), 10) || 1
            }).catch(function (err) { console.warn('[PGU] addToCart failed', err); });
            return;
          }

          // Preview / no-Shopify fallback.
          // price attr is in dollars; convert to cents for the formatter.
          var dollars = parseFloat(btn.getAttribute('data-price'));
          previewAdd({
            id: btn.getAttribute('data-id') || btn.getAttribute('data-slug') ||
                btn.getAttribute('data-title'),
            title: btn.getAttribute('data-title') || 'Product',
            price: isNaN(dollars) ? 0 : Math.round(dollars * 100),
            img: btn.getAttribute('data-img') || '',
            qty: parseInt(btn.getAttribute('data-qty'), 10) || 1
          });
        });
      });

      // If we're on a real Shopify storefront, hydrate the drawer once.
      if (hasShopify()) {
        refreshCart().catch(function () { /* non-fatal on first load */ });
      }
    }

    return {
      bind: bind,
      addToCart: addToCart,
      refreshCart: refreshCart,
      changeLine: changeLine,
      _preview: previewAdd
    };
  })();

  /* ------------------------------------------------------------------ */
  /* MODULE: Footer year + smooth in-page anchors (small niceties)       */
  /* ------------------------------------------------------------------ */
  function initMisc() {
    // Auto-fill any [data-pgu-year] element.
    $$('[data-pgu-year]').forEach(function (el) {
      el.textContent = String(new Date().getFullYear());
    });
  }

  /* ------------------------------------------------------------------ */
  /* BOOTSTRAP                                                            */
  /* ------------------------------------------------------------------ */
  function init() {
    initHeader();
    initMobileNav();
    initDrawer();
    initEscClose();
    initReveals();
    initParallax();
    initAccordions();
    initQtySteppers();
    initGallery();
    initZoom();
    initMarquee();
    Cart.bind();
    initMisc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose the cart API for theme/preview scripts that need it.
  window.PGU = window.PGU || {};
  window.PGU.cart = Cart;
})();
